import type { StyledComponent } from '@emotion/styled'
import {
  Box,
  ButtonBase,
  type ButtonBaseProps,
  styled,
  Typography,
} from '@mui/material'
import type { FC } from 'react'

export interface CardSelectRootProps {
  selected: boolean
}

export const CardSelectRoot: StyledComponent<
  ButtonBaseProps & CardSelectRootProps
> = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<CardSelectRootProps>(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  border: '1px solid',
  borderColor: selected
    ? theme.vars.palette.primary.main
    : theme.vars.palette.grey[300],
  backgroundColor: theme.vars.palette.background.paper,
  textAlign: 'left',
  transition: theme.transitions.create(['border-color', 'opacity'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:not(.Mui-disabled):hover': {
    borderColor: selected
      ? theme.vars.palette.primary.main
      : `color-mix(in srgb, ${theme.vars.palette.common.onBackground} 32%, transparent)`,
    backgroundColor: theme.vars.palette.background.paper,
  },
  '&.Mui-disabled': {
    opacity: 0.45,
  },
  ...theme.applyStyles('dark', {
    borderColor: selected
      ? theme.vars.palette.primary.main
      : theme.vars.palette.grey[800],
    '&:not(.Mui-disabled):hover': {
      borderColor: selected
        ? theme.vars.palette.primary.main
        : `color-mix(in srgb, ${theme.vars.palette.common.onBackground} 48%, transparent)`,
    },
  }),
}))

export const CardTitle: FC<React.ComponentProps<typeof Typography>> = styled(
  Typography
)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.vars.palette.text.primary,
  width: '100%',
}))

export const CardDescription: FC<React.ComponentProps<typeof Typography>> =
  styled(Typography)(({ theme }) => ({
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.vars.palette.text.secondary,
    width: '100%',
  }))

export const CardTextContainer: FC<React.ComponentProps<typeof Box>> = styled(
  Box
)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  width: '100%',
}))
